import { createDrawingCanvas, getDrawingContext2d } from '@/core/drawing-canvas'
import { performanceDiagnostics } from './performance-diagnostics'

export interface SourceFrame { readonly id: number; readonly capturedAt: number }

type FirefoxVideoElement = HTMLVideoElement & {
  webkitDecodedFrameCount?: number
  mozPresentedFrames?: number
}

function usableFrameCount(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

/** One identity across regular and priority sampling; retains metadata only. */
export class VideoFrameSource {
  private readonly releaseDiagnostic = performanceDiagnostics.acquire('frame-sources')
  private callbackId: number | null = null
  private disposed = false
  private counter: number | null = null
  private presented: number | null = null
  private fingerprint: number | null = null
  private current: SourceFrame | null = null
  private lastCallbackAt = Date.now()
  private lastFingerprintAt = -Infinity
  private fallbackSinceCallback = false

  constructor(private readonly video: HTMLVideoElement, private readonly fallbackDelayMs: () => number = () => 500) {
    this.read()
    this.subscribe()
  }

  read(): SourceFrame | null {
    if (this.disposed || !this.video.videoWidth || !this.video.videoHeight) return null
    const counter = this.frameCount()
    if (counter !== null) {
      if (this.counter === null || counter !== this.counter) this.advance()
      this.counter = counter
    } else if (Date.now() - this.lastFingerprintAt >= 500) {
      const fallbackAllowed = !this.video.requestVideoFrameCallback || Date.now() - this.lastCallbackAt >= this.fallbackDelayMs()
      if (!fallbackAllowed && this.fingerprint !== null) return this.current
      const fingerprint = this.imageFingerprint()
      if (fallbackAllowed && fingerprint !== null && fingerprint !== this.fingerprint
        && (!this.video.requestVideoFrameCallback || this.fingerprint !== null)) {
        this.advance()
        this.fallbackSinceCallback = true
      }
      this.fingerprint = fingerprint
      this.lastFingerprintAt = Date.now()
    }
    return this.current
  }

  async waitAfter(id: number, signal: AbortSignal): Promise<SourceFrame | null> {
    while (!signal.aborted && !this.disposed) {
      const frame = this.read()
      if (frame && frame.id > id) return frame
      await new Promise<void>((resolve) => {
        const done = () => { clearTimeout(timer); signal.removeEventListener('abort', done); resolve() }
        const timer = setTimeout(done, 50)
        signal.addEventListener('abort', done, { once: true })
      })
    }
    return null
  }

  dispose(): void {
    this.releaseDiagnostic()
    this.disposed = true
    if (this.callbackId !== null) this.video.cancelVideoFrameCallback?.(this.callbackId)
    this.callbackId = null
  }

  private subscribe(): void {
    if (this.disposed || !this.video.requestVideoFrameCallback) return
    this.callbackId = this.video.requestVideoFrameCallback((_now, metadata) => {
      this.callbackId = null
      if (this.disposed) return
      const count = this.frameCount()
      const presented = usableFrameCount(metadata.presentedFrames)
      // A fallback may already have observed this frame. Counters take precedence when they actually move.
      const fallbackAlreadySawFrame = count === null && this.fallbackSinceCallback && this.fingerprint !== null
        && this.imageFingerprint() === this.fingerprint
      const countMoved = count !== null && count !== this.counter
      const presentedMoved = presented !== null && presented !== this.presented
        && (count === null || this.presented !== null)
      const identityMoved = countMoved || presentedMoved || (count === null && presented === null)
      if (!fallbackAlreadySawFrame && identityMoved) this.advance()
      this.counter = count
      this.presented = presented
      this.lastCallbackAt = Date.now()
      this.fallbackSinceCallback = false
      this.fingerprint = null
      this.lastFingerprintAt = -Infinity
      this.subscribe()
    })
  }

  private advance(): void {
    this.current = { id: (this.current?.id ?? 0) + 1, capturedAt: Math.max(Date.now(), (this.current?.capturedAt ?? 0) + 1) }
  }

  private frameCount(): number | null {
    try {
      const element = this.video as FirefoxVideoElement
      return usableFrameCount(element.getVideoPlaybackQuality?.().totalVideoFrames)
        ?? usableFrameCount(element.webkitDecodedFrameCount)
        ?? usableFrameCount(element.mozPresentedFrames)
    } catch { return null }
  }

  private imageFingerprint(): number | null {
    try {
      const canvas = createDrawingCanvas(16, 9)
      const context = getDrawingContext2d(canvas, { willReadFrequently: true })
      context.drawImage(this.video, 0, 0, 16, 9)
      const bytes = context.getImageData(0, 0, 16, 9).data
      let hash = 2_166_136_261
      for (const byte of bytes) hash = Math.imul(hash ^ byte, 16_777_619)
      return hash >>> 0
    } catch { return null }
  }
}
